import classNames from 'classnames';
import ResultOverlay from 'components/overlay/result/ResultOverlay';
import { LAYER, LAYER_PROPS, MAP_OPTIONS } from 'constants/Constants';
import { OlDefaultView } from 'data/Ol';
import { useAddress } from 'hooks/feature/useAddress';
import { useOpenLayers } from 'hooks/ol/useOpenLayers';
import { MapBrowserEvent, Map as OlMap, Overlay, View } from 'ol';
import { defaults } from 'ol/control/defaults';
import { Coordinate } from 'ol/coordinate';
import { FeatureLike } from 'ol/Feature';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { OSM, XYZ } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { mapStore } from 'stores/Map';
import { PanelStateStore } from 'stores/PanelState';
import { convert3857To4326 } from 'utils/OlUtil';
import MeasureControl from './measureControl/MeasureControl';
import styles from './OlLayer.module.scss';
import ViewControl from './viewControl/ViewControl';
import ZoomControl from './zoomControl/ZoomControl';

const OlLayer = () => {
    const mapRef = useRef<HTMLDivElement>(null);
    const { map, setMap, setVectorSource, actions, setBaseImageLayer, setBaseImageSource, setCompareImageLayer, setCompareImageSource } = mapStore();
    const { fetchAddress } = useAddress();
    const { clearPoiLayer } = useOpenLayers();
    const { isOpen } = PanelStateStore();

    useEffect(() => {
        initOlMap();
    }, []);

    useEffect(() => {
        if (!map) return;
        map.on('singleclick', onClickFeatures);
    }, [map])

    const initTileLayer = () => {
        return new TileLayer({
            source: new OSM({ attributions: '' }),
        });
    };

    const initBaseImageLayer = () => {
        const source = new XYZ({ url: '', maxZoom: MAP_OPTIONS.TILE_MAX_ZOOM });
        const layer = new TileLayer({ source });
        setBaseImageSource(source);
        setBaseImageLayer(layer);
        return layer;
    };

    const initCompareImageLayer = () => {
        const source = new XYZ({ url: '', maxZoom: MAP_OPTIONS.TILE_MAX_ZOOM });
        const layer = new TileLayer({ source });
        setCompareImageSource(source);
        setCompareImageLayer(layer);
        return layer;
    };

    const initVectorLayer = () => {
        const source = new VectorSource({ wrapX: false });
        setVectorSource(source);
        return new VectorLayer({ source });
    };

    // const initGLLayer = (type: TLayerFactory, subName: string = '') => {
    //     const source = new VectorSource({ wrapX: false });
    //     source.setProperties({ name: `${type}${subName}` });
    //     actions[`${type}Source`](source);
    //     const layer = new WebGLLayerFactory().getGLLayerByType(type, { source });
    //     actions[`${type}Layer`](layer);
    //     return layer;
    // };

    const initOlMapParam = () => {
        return {
            controls: defaults({ zoom: false, rotate: false, attribution: false }),
            layers: [
                initTileLayer(),
                initBaseImageLayer(),
                initCompareImageLayer(),
                initVectorLayer(),
                // initGLLayer(LAYER_FACTORY.ROAD),
                // initGLLayer(LAYER_FACTORY.SEGMENTATION),
                // initGLLayer(LAYER_FACTORY.OBJECT),
                // initGLLayer(LAYER_FACTORY.CHANGE),
            ],
            view: new View(OlDefaultView),
        };
    };

    const initOlMap = () => {
        const map = new OlMap(initOlMapParam());
        map.setTarget(mapRef.current || '');
        // (window as any).map = map;
        setMap(map);

        return () => {
            map.setTarget('');
        };
    };

    const onClickFeatures = (event: MapBrowserEvent<MouseEvent>) => {
        if (!event.map) return;
        event.map.getOverlays().clear();
        clearPoiLayer();
        event.map.forEachFeatureAtPixel(event.pixel, async function (feature, layer) {
            if (!layer) return;
            if (layer.get(LAYER_PROPS.LAYER_TYPE) === LAYER.POI_LAYER || layer.get(LAYER_PROPS.LAYER_TYPE) === LAYER.MEASURE_LAYER) return;
            const vec2 = event.coordinate;
            const resolution = event.map.getView().getResolution();
            resolution && (vec2[1] += (8 * resolution));
            const roadAddress = await getRoadAddress(event.coordinate);
            event.map.addOverlay(drawOverlay(feature, event.coordinate, roadAddress));
            return true;
        });
    }

    const drawOverlay = (feature: FeatureLike, position: Coordinate, roadAddress: string | null) => {
        const overlayContainer = document.createElement('div');
        const overlayRoot = ReactDOM.createRoot(overlayContainer);
        overlayRoot.render(<ResultOverlay feature={feature} coordinates={convert3857To4326(position)} roadAddress={roadAddress} />);

        return new Overlay({
            element: overlayContainer,
            position,
            positioning: 'bottom-center',
        });
    };

    const getRoadAddress = async (coordinate: Coordinate) => {
        const convertedCoordinate = convert3857To4326(coordinate);
        const address = await fetchAddress(convertedCoordinate[0], convertedCoordinate[1]);
        return address.data.documents.length > 0 && address.data.documents[0].road_address ? address.data.documents[0].road_address.address_name : null;
    }

    return (
        <div className={classNames(styles.container, isOpen ? styles.shift : '')} ref={mapRef}>
            <ZoomControl />
            <ViewControl />
            <MeasureControl />
            <div id="map-slider-root" className={styles['map-slider-root']}></div>
        </div>
    );
};

export default OlLayer;
